import { Box } from '@mui/material';
import { type JSX, useContext, useEffect, useState } from 'react';
import { generatePath } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { Accordion } from 'Components/Accordion/Accordion';
import { AccordionCollapse } from 'Components/Accordion/AccordionCollapse/AccordionCollapse';
import { AccordionToggle } from 'Components/Accordion/AccordionToggle/AccordionToggle';
import { Alert } from 'Components/Alert/Alert';
import { GenerativeAiDisclaimer } from 'Components/ArtificialIntelligence/GenerativeAiDisclaimer';
import { Button } from 'Components/Buttons/Buttons';
import { Checkbox } from 'Components/Checkbox/Checkbox';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { RBACComponent } from 'Components/Context/RBACComponent';
import { RBACContext, Role, userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { FileManagementArea, FileManagementAreaRestriction, useFileManagementArea } from 'Components/Files/FileManagementArea';
import { FormFieldDatePicker } from 'Components/FormField/FormFieldDatePicker/FormFieldDatePicker';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Text } from 'Components/Text/Text';
import { LinkButtonToast, TextToast } from 'Components/Toast/Toast';
import { AI_DOC_ANALYZER_DETAILS, AI_DOC_ANALYZER_LISTING } from 'Config/PathTemplates';
import { AI_DOC_ANALYZER_CREATE_EXCEPTIONS, AI_DOC_ANALYZER_DOCUMENT_TYPE, AI_DOC_ANALYZER_FILE_UPLOAD, AI_DOC_ANALYZER_THIRD_PARTY_FOLDER, getAiDocAnalyzerExpirationTooltip, getAiDocAnalyzerThirdPartyTooltip } from 'Config/Tooltips';
import { jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { AiDocAnalysis, AiDocAnalyzerCreateRequest, AiDocAnalyzerType } from 'Models/ArtificialIntelligence';
import { AdvancedFeature } from 'Models/ClientDetailsModel';
import { FileToBeUploaded } from 'Models/Files';
import { FolderVersion } from 'Models/Folders';
import { ActiveState } from 'Models/Organization';
import { ThirdPartyResponseWithServices } from 'Models/TPRM';
import { OptionType } from 'Models/Types/GlobalType';

import styles from './AiDocAnalyzer.module.css';

const genericDocumentTypeOption: OptionType = { label: AiDocAnalyzerType.GENERIC, value: AiDocAnalyzerType.GENERIC };
const questionnaireDocumentTypeOption: OptionType = { label: AiDocAnalyzerType.QUESTIONNAIRE, value: AiDocAnalyzerType.QUESTIONNAIRE };
const socReportDocumentTypeOption: OptionType = { label: AiDocAnalyzerType.SYSTEM_AND_ORGANIZATION_CONTROLS_REPORT, value: AiDocAnalyzerType.SYSTEM_AND_ORGANIZATION_CONTROLS_REPORT };

const documentTypeDescriptions: Record<AiDocAnalyzerType, string> = {
    [AiDocAnalyzerType.GENERIC]: 'The document will be analyzed and summarized, extracting key information and insights to create a clear, structured summary.',
    [AiDocAnalyzerType.QUESTIONNAIRE]: "Your organization's governance documents will be used to generate responses to the questionnaire. Only governance documents that are stored in SummIT will be used to generate responses.",
    [AiDocAnalyzerType.SYSTEM_AND_ORGANIZATION_CONTROLS_REPORT]: 'The SOC report will be analyzed to extract and identify key information about the organization, especially related to control testing, complementary end-user controls, and sub-services.',
};

export interface AiDocAnalyzerCreateProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    documentApi: DocumentApi;
    tprmApi: ClientTPRMApi;
}

export const AiDocAnalyzerCreate = (props: AiDocAnalyzerCreateProps): JSX.Element => {
    // Dependent data.
    const cachedData = useCachedData();
    const rbacContext = useContext(RBACContext);
    const [thirdParties, setThirdParties] = useState<ThirdPartyResponseWithServices[]>([]);
    const [thirdPartyFolders, setThirdPartyFolders] = useState<FolderVersion[]>([]);

    // Form state.
    const [aiDocAnalysis, setAiDocAnalysis] = useState<AiDocAnalysis>();
    const [isGettingThirdPartyFolders, setIsGettingThirdPartyFolders] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [successMessage, setSuccessMessage] = useState<string>();
    const [failureMessage, setFailureMessage] = useState<string>();

    // Form fields.
    const [documentType, setDocumentType] = useState<AiDocAnalyzerType>(() => {
        if (cachedData.clientHasAdvancedFeature([AdvancedFeature.AI_DOCUMENT_ANALYZER_SOC])) {
            return AiDocAnalyzerType.SYSTEM_AND_ORGANIZATION_CONTROLS_REPORT;
        } else if (cachedData.clientHasAdvancedFeature([AdvancedFeature.AI_DOCUMENT_ANALYZER_QUESTIONNAIRE])) {
            return AiDocAnalyzerType.QUESTIONNAIRE;
        }
        return AiDocAnalyzerType.GENERIC;
    });
    const [createExceptions, setCreateExceptions] = useState<boolean>(false);
    const [expirationDate, setExpirationDate] = useState<Date>();
    const [thirdParty, setThirdParty] = useState<string>();
    const [thirdPartyFolder, setThirdPartyFolder] = useState<string>();
    const [submitRequestWithManagedFiles, fileManagementHookValues] = useFileManagementArea(props.documentApi, [], FileManagementAreaRestriction.UP_TO_ONE_FILE);

    /** Get active Third Parties. */
    useEffect(() => {
        const getThirdParties = async (): Promise<void> => {
            try {
                const response = await props.tprmApi.getThirdParties();
                setThirdParties(response.data.filter((thirdParty) => thirdParty.active_state === ActiveState.ACTIVE));
            } catch (error) {
                setFailureMessage(error);
            }
        };

        if (rbacContext && userHasAuthorizedRole(rbacContext.roles, [Role.THIRD_PARTY_USER])) {
            getThirdParties();
        }
    }, [props.tprmApi, rbacContext]);

    /** Get Third-Party Folders for the selected Third Party. */
    useEffect(() => {
        const getThirdPartyFolders = async (): Promise<void> => {
            try {
                setIsGettingThirdPartyFolders(true);
                if (thirdParty) {
                    const response = await props.tprmApi.getAllCurrentFoldersForThirdParty(thirdParty);
                    setThirdPartyFolders(response.data);
                }
            } catch (error) {
                setFailureMessage(error);
            } finally {
                setIsGettingThirdPartyFolders(false);
            }
        };

        if (rbacContext && userHasAuthorizedRole(rbacContext.roles, [Role.THIRD_PARTY_USER])) {
            getThirdPartyFolders();
        }
    }, [props.tprmApi, thirdParty, rbacContext]);

    /**
     * Handler for when the Third Party selection changes.
     * When selecting a Third Party, set default values for dependent fields.
     * When deselecting a Third Party, clear values for dependent fields since they are not relevant without a Third Party selected.
     */
    const handleThirdPartyChange = (value: ChangeEventType): void => {
        const newThirdParty = value as string | undefined;
        setThirdParty(newThirdParty);

        if (thirdParty === undefined && newThirdParty !== undefined && documentType === AiDocAnalyzerType.SYSTEM_AND_ORGANIZATION_CONTROLS_REPORT) {
            setCreateExceptions(true);
            const oneYearFromNow = new Date();
            oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
            setExpirationDate(oneYearFromNow);
        } else if (thirdParty !== undefined && newThirdParty === undefined) {
            setCreateExceptions(false);
            setExpirationDate(undefined);
            setThirdPartyFolder(undefined);
        }
    };

    /**
     * Handler for when the Third-Party Folder selection changes.
     * Clear the expiration date if the Third-Party Folder is cleared and the Create Exceptions checkbox is unchecked, since the expiration date is only relevant to those two.
     * Set the expiration date to a default value if a Third-Party Folder is selected and the expiration date is not already set.
     */
    const handleThirdPartyFolderChange = (value: ChangeEventType): void => {
        const newThirdPartyFolder = value as string | undefined;
        setThirdPartyFolder(newThirdPartyFolder);

        if (newThirdPartyFolder === undefined && !createExceptions) {
            setExpirationDate(undefined);
        } else if (thirdPartyFolder === undefined && newThirdPartyFolder !== undefined && expirationDate === undefined) {
            const oneYearFromNow = new Date();
            oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
            setExpirationDate(oneYearFromNow);
        }
    };

    /**
     * Handler for when the Create Exceptions checkbox changes.
     * Clear the expiration date if the Create Exceptions checkbox is cleared and the Third-Party Folder is blank, since the expiration date is only relevant to those two.
     * Set the expiration date to a default value if the Create Exceptions checkbox is checked and the expiration date is not already set.
     */
    const handleCreateExceptionsChange = (isChecked: boolean): void => {
        setCreateExceptions(isChecked);

        if (!isChecked && thirdPartyFolder === undefined) {
            setExpirationDate(undefined);
        } else if (!createExceptions && isChecked && expirationDate === undefined) {
            const oneYearFromNow = new Date();
            oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
            setExpirationDate(oneYearFromNow);
        }
    };

    /**
     * Handler for when the Document Type selection changes.
     * Clear the Create Exceptions checkbox if Exceptions are not relevant to the document type that is selected.
     * Clear the expiration date if the Create Exceptions checkbox is unchecked and a Third-Party Folder is not selected.
     * Set a default expiration date if switching to a document type where Exceptions are relevant and a Third Party is selected.
     */
    const handleDocumentTypeChange = (value: ChangeEventType): void => {
        const newDocumentType = value as AiDocAnalyzerType;
        setDocumentType(newDocumentType);

        if (newDocumentType === AiDocAnalyzerType.GENERIC) {
            setCreateExceptions(false);
            if (thirdPartyFolder === undefined) {
                setExpirationDate(undefined);
            }
        } else if (newDocumentType === AiDocAnalyzerType.SYSTEM_AND_ORGANIZATION_CONTROLS_REPORT && thirdParty !== undefined) {
            setCreateExceptions(true);
            if (expirationDate === undefined) {
                const oneYearFromNow = new Date();
                oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
                setExpirationDate(oneYearFromNow);
            }
        }
    };

    /** Handler for when the submit button is clicked. Includes handling file submission, if applicable. */
    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        setFailureMessage(undefined);
        setSuccessMessage(undefined);
        setIsSubmitting(true);

        try {
            await submitRequestWithManagedFiles(async (fileUpdates) => await submitRequest(fileUpdates.new_files[0]));
            setSuccessMessage('The document is being analyzed. Refresh the analysis page in a few minutes to see the results.');
            setIsSubmitted(true);
        } catch (error) {
            setFailureMessage(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    /** Submit the form to the back-end API to analyze and summarize the document. */
    const submitRequest = async (newFile: FileToBeUploaded) => {
        // Validation.
        if (!documentType) {
            throw new Error('Document type is required.');
        }
        if (!newFile) {
            throw new Error('Document is required.');
        }
        if (expirationDate !== undefined && expirationDate <= new Date()) {
            throw new Error('Expiration date must be in the future.');
        }

        const request: AiDocAnalyzerCreateRequest = {
            document_type: documentType,
            input_file_id: newFile.file_id,
            input_filename: newFile.filename,

            // Attributes that are relevant for AiDocAnalyzerType.SYSTEM_AND_ORGANIZATION_CONTROLS_REPORT
            create_exceptions: createExceptions,
            expiration_date: expirationDate ? jsDateToIso8601(expirationDate) : undefined,
            third_party_id: thirdParty,
            third_party_folder_id: thirdPartyFolder,
        };
        const response = await props.artificialIntelligenceApi.aiDocAnalyzerCreate(request);
        setAiDocAnalysis(response.data);
    };

    const hasQuestionnaireFeature = cachedData.clientHasAdvancedFeature([AdvancedFeature.AI_DOCUMENT_ANALYZER_QUESTIONNAIRE]);
    const hasSocReportFeature = cachedData.clientHasAdvancedFeature([AdvancedFeature.AI_DOCUMENT_ANALYZER_SOC]);
    const documentTypeOptions: OptionType[] = [genericDocumentTypeOption, ...(hasQuestionnaireFeature ? [questionnaireDocumentTypeOption] : []), ...(hasSocReportFeature ? [socReportDocumentTypeOption] : [])];
    const disableForm = isSubmitting || isSubmitted;
    const disableCreateExceptions = disableForm || !thirdParty;
    const disableThirdPartyFolder = disableForm || !thirdParty || isGettingThirdPartyFolders;
    const isSocReport = documentType === AiDocAnalyzerType.SYSTEM_AND_ORGANIZATION_CONTROLS_REPORT;
    const disableExpirationDate = disableForm || !thirdParty || (!createExceptions && !thirdPartyFolder);
    const pointlessThirdParty = thirdParty && !createExceptions && !thirdPartyFolder;

    return (
        <>
            {successMessage && aiDocAnalysis && <LinkButtonToast variant="success" clearToast={() => setSuccessMessage(undefined)} linkButtonText={'View Analysis'} linkButtonTo={generatePath(AI_DOC_ANALYZER_DETAILS, { id: aiDocAnalysis.id })} text={successMessage} />}
            {failureMessage && <TextToast variant="failure" clearToast={() => setFailureMessage(undefined)} text={failureMessage} />}
            <PageLayoutDefault
                headerTitle="Create Analysis"
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={AI_DOC_ANALYZER_LISTING}>Document Analyzer</BreadcrumbLink>
                        <BreadcrumbText>Create Analysis</BreadcrumbText>
                    </Breadcrumb>
                }
                body={[
                    {
                        content: (
                            <Box component="form" noValidate onSubmit={handleSubmit}>
                                <Text>Select a document type and upload a document to analyze. The "Third-Party Risk Management" section below provides optional integration with features of the Third-Party Risk Management module. </Text>
                                <div className={styles.fieldOneHalfWidth}>
                                    <FormFieldSelect disabled={disableForm} formFieldId="documentType" formFieldLabel="Document Type" options={documentTypeOptions} selectedOption={documentType} handleChange={handleDocumentTypeChange} required notClearable tooltip={AI_DOC_ANALYZER_DOCUMENT_TYPE} />
                                </div>
                                <div className={styles.textMargin}>
                                    <Text noStyles>{documentTypeDescriptions[documentType]}</Text>
                                </div>

                                <FileManagementArea disabled={disableForm} documentApi={props.documentApi} fileManagementHookValues={fileManagementHookValues} fileDragAndDropFormFieldLabel="Document" tooltip={AI_DOC_ANALYZER_FILE_UPLOAD} />
                                <RBACComponent roles={[Role.THIRD_PARTY_USER]}>
                                    <Accordion defaultActiveKey="thirdPartyRiskManagement">
                                        <div className={styles.accordionHeader}>
                                            <AccordionToggle eventKey="thirdPartyRiskManagement" ariaLabelSuffix="Third-Party Risk Management" />
                                            <Text noStyles variant="Header2">
                                                Third-Party Risk Management
                                            </Text>
                                        </div>
                                        <hr />
                                        <AccordionCollapse eventKey="thirdPartyRiskManagement">
                                            <>
                                                {pointlessThirdParty && <Alert type="warning">Selecting a third party has no effect without also selecting a third-party folder{isSocReport ? ' or creating exceptions' : ''}.</Alert>}
                                                <div className={styles.fieldContainer}>
                                                    <div className={styles.fieldOneThirdWidth}>
                                                        <FormFieldSelect disabled={disableForm} formFieldId="thirdParty" formFieldLabel="Third Party" handleChange={handleThirdPartyChange} options={thirdParties.map((thirdParty) => ({ value: thirdParty.id, label: thirdParty.name }))} selectedOption={thirdParty} tooltip={getAiDocAnalyzerThirdPartyTooltip(isSocReport)} />
                                                    </div>
                                                    <div className={styles.fieldOneThirdWidth}>
                                                        {/* The `key` prop is used to reset the component when the Third Party is cleared, otherwise the old thirdPartyFolder value is still visible in the UI despite thirdPartyFolder being undefined in state. */}
                                                        <FormFieldSelect key={thirdParty ?? 'none'} disabled={disableThirdPartyFolder} formFieldId="thirdPartyFolder" formFieldLabel="Third-Party Folder" handleChange={handleThirdPartyFolderChange} options={thirdPartyFolders.map((thirdPartyFolder) => ({ value: thirdPartyFolder.id, label: thirdPartyFolder.name }))} selectedOption={thirdPartyFolder} tooltip={AI_DOC_ANALYZER_THIRD_PARTY_FOLDER} />
                                                    </div>
                                                    {isSocReport && (
                                                        <div className={`${styles.fieldOneThirdWidth} ${styles.fieldAlignBottom}`}>
                                                            <Checkbox checked={createExceptions} disabled={disableCreateExceptions} label="Create Exceptions" onToggled={handleCreateExceptionsChange} tooltip={AI_DOC_ANALYZER_CREATE_EXCEPTIONS} />
                                                        </div>
                                                    )}
                                                    <div className={styles.fieldOneThirdWidth}>
                                                        <FormFieldDatePicker disabled={disableExpirationDate} formFieldId="expirationDate" formFieldLabel="Expiration Date" handleChange={(date: Date) => setExpirationDate(date)} selected={expirationDate} tooltip={getAiDocAnalyzerExpirationTooltip(isSocReport)} />
                                                    </div>
                                                </div>
                                                <hr className={styles.hrMargin} />
                                            </>
                                        </AccordionCollapse>
                                    </Accordion>
                                </RBACComponent>
                                <GenerativeAiDisclaimer />
                                <div>
                                    <Button disabled={disableForm} isLoading={isSubmitting} loadingText="Submitting..." variant="submit">
                                        Submit
                                    </Button>
                                </div>
                            </Box>
                        ),
                    },
                ]}
            />
        </>
    );
};
